/** @file artBriefOf: the Markdown brief an image model draws from, one section per drawable (design 20). */
import { briefCamera } from './briefCamera';
import { briefLine } from './briefLine';
import type { ViewedSlot } from './artTypes';

/** The profile line of the brief: its id and camera pitch (degrees above the horizon; null when unknown). */
export type ArtBriefProfile = { id: string; pitchDeg: number | null };

/** `includeFilled` lists slots that already have an image too (default: only the empty ones). */
export type ArtBriefOptions = { includeFilled?: boolean };

const INTRO = [
  '# Art brief',
  '',
  'Draw one PNG for every line below. Use the file name and the exact size given, a transparent',
  'background, and the same character, palette and proportions in every file. The joint is where',
  'the part is pinned to the skeleton: keep it on the pixel given. Left and right are the',
  "character's own.",
];

/** The slots grouped by drawable (first appearance order), each group in entry then tile order. */
function byDrawable(slots: readonly ViewedSlot[]): Map<string, ViewedSlot[]> {
  const out = new Map<string, ViewedSlot[]>();
  for (const s of slots) out.set(s.drawable, [...(out.get(s.drawable) ?? []), s]);
  for (const list of out.values()) list.sort((a, b) => a.entry - b.entry || a.tile - b.tile);
  return out;
}

/**
 * The brief as Markdown: intro, the style paragraph, the camera, then a section per drawable with one line per slot
 * (`briefLine`); filled slots are left out unless `includeFilled`. Pure: the same slots give the same text.
 */
export function artBriefOf(slots: readonly ViewedSlot[], style: string, profile: ArtBriefProfile, options: ArtBriefOptions = {}): string {
  const shown = slots.filter((s) => options.includeFilled === true || s.status === 'empty');
  const lines = [...INTRO, '', '## Style', '', style.trim() === '' ? 'No style notes.' : style.trim(), '', ...briefCamera(profile)];
  if (shown.length === 0) return [...lines, '', 'Nothing to draw: every slot is filled.', ''].join('\n');
  for (const [drawable, list] of byDrawable(shown)) lines.push('', `## ${drawable}`, '', ...list.map((s) => briefLine(s, profile.pitchDeg)));
  return [...lines, ''].join('\n');
}
